<section class="gp" id="global-presence" #gpRoot>
  <!-- decorative layers -->
  <div class="gp__deco" aria-hidden="true">
    <span class="gp__blob gp__blob--1"></span>
    <span class="gp__blob gp__blob--2"></span>
    <span class="gp__mesh"></span>
  </div>

  <div class="container gp__inner">
    <!-- ============ hero ============ -->
    <div class="gp__hero">
      <!-- ---------- left: copy + stats ---------- -->
      <div class="gp__copy" [@gpFade]="heroState()">
        <span class="gp__badge">
          <span class="gp__badge-dot" aria-hidden="true"></span>
          Global Presence
        </span>

        <h2 class="gp__title">
          Building Digital Solutions
          <span class="gp__title-grad">Across the Globe</span>
        </h2>

        <p class="gp__lede">
          We collaborate with businesses worldwide to deliver scalable software
          solutions, enterprise applications, AI products, and digital
          transformation services.
        </p>

        <ul class="gp__stats" [@gpStats]="heroState()">
          <li
            class="gp-stat"
            *ngFor="let s of stats; trackBy: trackByStat"
            [attr.data-tone]="s.tone"
          >
            <span class="gp-stat__ico" aria-hidden="true">
              <svg
                *ngIf="s.icon === 'globe'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
              </svg>
              <svg
                *ngIf="s.icon === 'handshake'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m11 17-4.5-4.5a3.5 3.5 0 0 1 5-5L13 9l1.5-1.5a3.5 3.5 0 0 1 5 5L15 17" />
                <path d="m3 10 4-4M21 10l-4-4" />
              </svg>
              <svg
                *ngIf="s.icon === 'rocket'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 15c-1.5 1.3-2 5.5-2 5.5s4.2-.5 5.5-2A2.2 2.2 0 0 0 5 15z" />
                <path d="m12.5 14.5-3-3a21 21 0 0 1 2-4A12.8 12.8 0 0 1 21 3c0 2.7-.8 7.4-6 10.9a21 21 0 0 1-2.5 1.6z" />
              </svg>
              <svg
                *ngIf="s.icon === 'star'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 3 4.5 6v6c0 4.5 3.2 8.3 7.5 9.5 4.3-1.2 7.5-5 7.5-9.5V6z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </span>
            <strong class="gp-stat__num">
              {{ counters()[s.id] }}<em>{{ s.suffix }}</em>
            </strong>
            <small class="gp-stat__label">{{ s.label }}</small>
          </li>
        </ul>
      </div>

      <!-- ---------- right: map ---------- -->
      <div class="gp__mapwrap" [@gpFade]="heroState()">
        <div class="gp-map__head">
          <span class="gp-map__live">
            <i aria-hidden="true"></i>Live delivery network
          </span>
          <span class="gp-map__meta">{{ regions.length }} regions &middot; 24/7 coverage</span>
        </div>

        <div class="gp-map">
          <span class="gp-map__glow" aria-hidden="true"></span>

          <!-- static, undistorted base map (Natural Earth, equirectangular) -->
          <img
            class="gp-map__base"
            src="/assets/images/world-dotted-map.svg"
            alt=""
            width="1000"
            height="386"
            loading="lazy"
            decoding="async"
            aria-hidden="true"
          />

          <!-- animated connection lines, same viewBox as the base map -->
          <svg
            class="gp-map__arcs"
            viewBox="0 0 1000 386"
            fill="none"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="gpArc" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#ff2d95" />
                <stop offset="50%" stop-color="#b14eff" />
                <stop offset="100%" stop-color="#6c63ff" />
              </linearGradient>
            </defs>
            <path
              class="gp-map__arc"
              *ngFor="let a of arcs; let i = index; trackBy: trackByArc"
              [class.is-hot]="isArcHot(a)"
              [style.--i]="i"
              [attr.d]="a.d"
              stroke="url(#gpArc)"
            />
          </svg>

          <!-- floating particles -->
          <span
            class="gp-map__mote"
            *ngFor="let m of motes"
            [style.left.%]="m.x"
            [style.top.%]="m.y"
            [style.--d]="m.d + 's'"
            aria-hidden="true"
          ></span>

          <!-- region pins -->
          <button
            type="button"
            class="gp-pin"
            *ngFor="let r of regions; let i = index; trackBy: trackByRegion"
            [class.is-hub]="r.hub"
            [class.is-hot]="hot === r.id"
            [attr.data-tone]="r.tone"
            [style.left.%]="r.x"
            [style.top.%]="r.y"
            [style.--i]="i"
            (mouseenter)="hot = r.id"
            (mouseleave)="hot = ''"
            (focus)="hot = r.id"
            (blur)="hot = ''"
          >
            <span class="gp-pin__pulse" aria-hidden="true"></span>
            <span class="gp-pin__dot" aria-hidden="true"></span>
            <span class="gp-pin__tip">{{ r.name }}</span>
          </button>
        </div>

        <!-- region legend, doubles as the map's hover control -->
        <ul class="gp-legend">
          <li
            class="gp-legend__item"
            *ngFor="let r of regions; trackBy: trackByRegion"
            [attr.data-tone]="r.tone"
            [class.is-hot]="hot === r.id"
            (mouseenter)="hot = r.id"
            (mouseleave)="hot = ''"
          >
            <i aria-hidden="true"></i>{{ r.name }}
          </li>
        </ul>
      </div>
    </div>

    <!-- ============ partner countries ============ -->
    <div class="gp__partners" #gpGrid>
      <div class="gp__partners-head">
        <h3 class="gp__partners-title">Our Partner Countries</h3>
        <span class="gp__partners-note">{{ countries.length }} active markets</span>
      </div>

      <ul class="gp__grid" [@gpCountries]="gridState()">
        <li
          class="gp-country"
          *ngFor="let c of countries; trackBy: trackByCode"
        >
          <span class="gp-country__flag">
            <img
              [src]="'/assets/countries-flag/svg/' + c.code + '.svg'"
              [alt]="c.name + ' flag'"
              width="24"
              height="16"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span class="gp-country__name">{{ c.name }}</span>
        </li>
      </ul>
    </div>
  </div>
</section>
